import { computed, observable, runInAction } from 'mobx';
import { RouterStore } from '@superwf/mobx-react-router';
import { createTransformer } from 'mobx-utils';
import { parse } from 'qs';
import { extractHyperlink, putSearchIfMissing, SearchParams } from '../util/NavigationUtil';
import { hasCurrentHost, hasFilePath, toPath, isSuperSelfService } from '../util/PathUtil';
import GaDataStore from './GaDataStore';
import RootStore from './RootStore';

class RouteStore {
  @observable accessor redirecting: boolean = false;

  private rootStore: RootStore;
  private routing: RouterStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.routing = rootStore.routing;
    this.setupGlobalRouting();
  }

  @computed
  public get pathname() {
    return this.getLocation().pathname || '/';
  }

  @computed
  private get path() {
    return this.pathname.split('/').filter((path) => path);
  }

  @computed
  get decodedParams() {
    return parse(this.location.search, { ignoreQueryPrefix: true }) as any;
  }

  public setToRedirect = () => {
    this.redirecting = true;
  };

  private setupGlobalRouting() {
    window.telia = {
      ...window.telia,
      navigateLocal: this.navigateLocal,
      navigate: this.navigate
    };
  }

  @computed
  get currentPathIsHomepage(): boolean {
    return this.pathnameWithoutLang() === '/';
  }

  public pathOnlyIncludesSegment(path: string[] = this.path) {
    const segments = this.pathWithoutLang(path);
    if (segments[0] === 'era' || segments[0] === 'ari' || !segments[0]) {
      return segments.length <= 1;
    }
    return false;
  }

  public navigate = (event: Event | React.SyntheticEvent) => {
    const isLocalNavigation = !isSuperSelfService(event.currentTarget) && hasCurrentHost(event.currentTarget) && !hasFilePath(event.currentTarget);
    if (isLocalNavigation) {
      this.navigateLocal(event);
    }
  };

  private navigateLocal(event: Event | React.SyntheticEvent) {
    event.preventDefault();
    GaDataStore.pushWithoutCmsData({ event: 'react-pageview' });
    const target = event.currentTarget;
    const languageStore = this.rootStore.languageStore;
    const pathname = languageStore.pathForCurrentLang(target.pathname);
    runInAction(() => this.routing.push(pathname + target.search + target.hash));
  }

  private pathIncludesLang(path: string[] = this.path) {
    return path && path.length > 0 && ['et', 'ru', 'en'].includes(path[0]);
  }

  public langFromPath() {
    return this.pathIncludesLang() ? this.path[0] : null;
  }

  public pathWithoutLang(path: string[] = this.path) {
    if (this.pathIncludesLang(path)) {
      return path.slice(1);
    }
    return path;
  }

  public pathnameWithoutLang(path: string[] = this.path) {
    return `/${this.pathWithoutLang(path).join('/')}`;
  }

  public updateLangInPath(lang: string, defaultLang: string) {
    const location = this.routing.location;
    if (location && this.langNeedsUpdating(lang, defaultLang)) {
      runInAction(() => this.routing.replace((lang !== defaultLang ? `/${lang}` : '') + this.pathnameWithoutLang() + location.search + location.hash));
    }
  }

  @computed
  get isOnCurrentPath() {
    return createTransformer((url: string) => this.isCurrentPath(url, false));
  }

  public isCurrentPath(url: string, exact: boolean = false) {
    const currentPath = this.pathnameWithoutLang();
    const urlWithoutLang = this.pathnameWithoutLang(toPath(url));
    return exact ? currentPath === urlWithoutLang : currentPath.startsWith(urlWithoutLang);
  }

  private langNeedsUpdating(lang: string, defaultLang: string) {
    return (lang !== defaultLang && this.langFromPath() !== lang) || (lang === defaultLang && this.pathIncludesLang());
  }

  public getLocation = () => this.routing.location || window.location;

  public push(url: string, search: SearchParams = null) {
    const link = extractHyperlink(url);
    putSearchIfMissing(link, search);
    GaDataStore.pushWithoutCmsData({ event: 'react-pageview' });
    runInAction(() => this.routing.push(url + link.search));
  }

  public replace(url: string) {
    GaDataStore.pushWithoutCmsData({ event: 'react-pageview' });
    const htmlLink = document.createElement('a');
    htmlLink.href = url;
    if (!isSuperSelfService({ href: htmlLink.host + htmlLink.pathname }) && htmlLink.host === window.location.host) {
      this.routing.replace(htmlLink.pathname + htmlLink.search + htmlLink.hash);
    } else {
      window.location.replace(url);
    }
  }

  @computed
  private get location() {
    return this.routing.location;
  }
}

export default RouteStore;
